import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AdminAiTesterModule } from 'src/app/admin/admin-ai-tester/admin-ai-tester.module';
import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { BulkManageChannelsComponent } from 'src/app/components/bulk-manage-channels/bulk-manage-channels.component';
import { BulkManageGroupsComponent } from 'src/app/components/bulk-manage-groups/bulk-manage-groups.component';
import { CollectionSelectorComponent } from 'src/app/components/collection-selector/collection-selector.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { UpsertCollectionModalComponent } from 'src/app/components/upsert-collection-modal/upsert-collection-modal.component';
import { FEATURES } from 'src/app/constants';
import { FilterOption, FilterValue, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { getWhereClausesFromFilters } from 'src/app/utils/utils';
import { Customer, KnowledgeBaseArticle, ListRequest, Tag, WhereClause } from 'src/models';
import { KnowledgeBaseCollection } from 'src/models/knowledge-base-collection.model';
import { UserGroup } from 'src/models/user-groups';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { KnowledgeBaseArticlesService } from 'src/services/knowledge-base-articles.service';
import { KnowledgeBaseCollectionsService } from 'src/services/knowledge-base-collections.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';
import { UserGroupsService } from 'src/services/user-groups.service';

// Combined interface for tree nodes
export interface HierarchyTreeNode {
  id: string;
  name: string;
  type: 'collection' | 'article' | 'create-action' | 'create-root-article-action';
  parentId?: string | null; // For collections, it's parent collection; for articles, it's collectionId
  children?: HierarchyTreeNode[]; // Only for collections
  level: number;
  expanded?: boolean;
  isLoadingChildren?: boolean;
  data?: KnowledgeBaseCollection | KnowledgeBaseArticle;
  updatedAt?: string | Date;
  tags?: { id: string; name: string }[];
  // Add other relevant properties from Collection/Article if needed
  // e.g., status for articles?
}

const ACTION_LABELS = {
  EDIT: 'Edit',
  MOVE: 'Move to...',
  DELETE: 'Delete',
  NEW_ARTICLE: 'New Article',
  NEW_COLLECTION: 'New Collection',
} as const;

@Component({
  selector: 'app-knowledge-base-hierarchy-page',
  templateUrl: './knowledge-base-hierarchy-page.component.html',
  styleUrls: ['./knowledge-base-hierarchy-page.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, FilterButtonComponent, AdminAiTesterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KnowledgeBaseHierarchyPageComponent implements OnInit, OnDestroy {
  searchTerm: string = '';
  filters: SelectedFilterMap = new Map();
  articleWhereClauses: WhereClause[] = [];
  filterOptions: any[] = [
    {
      label: 'Article Status',
      id: 'articleStatus',
      icon: 'document-text',
      type: 'select',
      values: [
        { label: 'Active', id: 'active' },
        { label: 'Inactive', id: 'inactive' },
      ],
    },
    /*
    Disabling this until publishing is released
    {
      label: 'Published Status',
      id: 'publishedAt',
      icon: 'calendar',
      type: 'select',
      values: [
        { label: 'Yes', id: 'published' },
        { label: 'No', id: 'not_published' },
      ],
    },
    */
    {
      label: 'Source',
      id: 'knowledgeBaseScrapeJobId',
      icon: 'sync',
      type: 'select',
    },
    {
      label: 'User Group',
      id: 'userGroupId',
      icon: 'people-outline',
      type: 'select',
    },
    {
      label: 'Channel',
      id: 'channelId',
      icon: 'chatbubble-outline',
      type: 'select',
      searchId: 'customerId',
    },
  ];

  isLoading = false;
  isMovingItem = false;
  treeNodes: HierarchyTreeNode[] = [];
  selectedNode: HierarchyTreeNode | null = null;
  selectedArticleIds = new Set<string>();

  allCollections: KnowledgeBaseCollection[] = [];
  currentArticles: Partial<KnowledgeBaseArticle>[] = [];

  // Map for efficient node lookup by ID (optimization)
  private nodeMap = new Map<string, HierarchyTreeNode>();

  currentlyHighlightedDropTarget: HTMLElement | null = null;

  // RxJS Subject for debouncing search input
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription;

  // Add back properties for buttons
  showRunQueryModal = false;
  featureEnabled = true;
  isTagManagementEnabled: boolean = false;

  sortField: string = 'name';
  sortDescending: boolean = false;

  // Import document properties
  showImportModal = false;
  isUploading = false;
  selectedFile: File | null = null;
  importOptions = {
    title: '',
    collectionId: null as string | null,
    extractImages: true,
    strategy: 'auto' as 'auto' | 'hi_res' | 'fast' | 'vlm' | 'ocr_only',
    vlmModelProvider: 'anthropic' as 'anthropic' | 'openai' | 'bedrock' | 'vertex',
  };

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  @HostBinding('class.has-active-footer') get footerIsActive(): boolean {
    return this.selectedArticleIds.size > 0;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private collectionsService: KnowledgeBaseCollectionsService,
    private articlesService: KnowledgeBaseArticlesService,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private cdRef: ChangeDetectorRef,
    private modalCtrl: ModalController,
    public authService: AuthService,
    private userGroupsService: UserGroupsService,
    private searchablePopoverService: SearchablePopoverService,
    private tagsService: TagsService,
    private customersService: CustomersService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.KNOWLEDGE_BASE_SYNC);

    await this.loadInitialData();
    this.cdRef.markForCheck();

    this.searchSubscription = this.searchSubject.pipe(debounceTime(300)).subscribe((searchTermValue) => {
      this.updateArticleWhereClauses();
      // Call loadInitialData WITHOUT passing expandedIds
      this.loadInitialData();
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onSearchChange(): void {
    // Push the current search term onto the subject
    this.searchSubject.next(this.searchTerm);
    // Don't call loadData directly here anymore
  }

  filtersDidChange(selected: SelectedFilterMap): void {
    this.filters = selected;
    this.updateArticleWhereClauses();

    const expandedIds = this.getExpandedCollectionIds();
    this.loadInitialData(expandedIds);
  }

  sortArticles(field: string): void {
    this.sortField = field;

    if (this.sortField === field) {
      this.sortDescending = !this.sortDescending;
    } else {
      this.sortDescending = true;
    }

    this.loadInitialData();
  }

  updateArticleWhereClauses(): void {
    const finalWheres: WhereClause[] = [];
    const collectedUserGroupIds: string[] = [];
    const collectedChannelIds: string[] = [];

    // Iterate through the selected filters map directly
    for (const [option, values] of this.filters.entries()) {
      // Handle special cases first
      if (option.id === 'publishedAt') {
        // Assuming only one publishedAt filter can be selected
        const selectedValue = values[0]?.id;
        if (selectedValue === 'published') {
          finalWheres.push({ field: 'publishedAt', operator: '>', value: 0 });
        } else if (selectedValue === 'not_published') {
          finalWheres.push({ field: 'publishedAt', operator: '==', value: null });
        }
      } else if (option.id === 'articleStatus') {
        // Assuming only one articleStatus filter can be selected
        const selectedValue = values[0]?.id;
        if (selectedValue) {
          finalWheres.push({ field: 'status', operator: '==', value: selectedValue });
        }
      } else if (option.id === 'userGroupId') {
        // Collect all selected user group IDs (user might add filter multiple times)
        values.forEach((value) => {
          if (typeof value.id === 'string') {
            // Ensure ID is string and not null/boolean
            collectedUserGroupIds.push(value.id);
          }
        });
      } else if (option.id === 'channelId') {
        values.forEach((value) => {
          if (typeof value.id === 'string') {
            collectedChannelIds.push(value.id);
          }
        });
      } else {
        // Use getWhereClausesFromFilters for standard simple filters (like Scrape Job ID)
        // Create a temporary map with just this one filter to process it
        const tempMap = new Map<FilterOption, FilterValue[]>();
        tempMap.set(option, values);
        const standardClauses = getWhereClausesFromFilters(tempMap);
        finalWheres.push(...standardClauses);
      }
    }

    // Add the collected userGroupIds filter if any were selected
    if (collectedUserGroupIds.length > 0) {
      finalWheres.push({
        field: 'userGroupIds',
        operator: 'contains',
        value: collectedUserGroupIds,
      });
    }

    if (collectedChannelIds.length > 0) {
      finalWheres.push({
        field: 'channelIds',
        operator: 'contains',
        value: collectedChannelIds,
      });
    }

    // Add search term if present
    if (this.searchTerm?.length > 0) {
      finalWheres.push({
        field: 'title',
        operator: 'like',
        value: `%${this.searchTerm}%`,
      });
    }

    this.articleWhereClauses = finalWheres;
  }

  async loadInitialData(expandedIdsToRestore?: Set<string>): Promise<void> {
    this.isLoading = true;
    this.nodeMap.clear();
    this.cdRef.markForCheck();

    try {
      const collectionsPromise = this.collectionsService.getAll({
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.authService.selectedProject?.id ?? null,
          },
        ],
      });
      const articleParams: ListRequest = {
        where: [
          ...this.articleWhereClauses,
          {
            field: 'projectId',
            operator: '==',
            value: this.authService.selectedProject?.id ?? null,
          },
        ],
        fields: ['id', 'title', 'collectionId', 'status', 'updatedAt', 'tags'],
        order: [this.sortField],
        descending: this.sortDescending,
      };
      const articlesPromise = this.articlesService.listAllStubs(articleParams);

      [this.allCollections, this.currentArticles] = await Promise.all([collectionsPromise, articlesPromise]);

      this.treeNodes = this.buildTree(this.allCollections, this.currentArticles, null, 0, this.searchTerm);

      if (this.searchTerm && this.searchTerm.length > 0) {
        // Search is active: Expand all remaining nodes
        this.expandAllNodes(this.treeNodes);
      } else if (expandedIdsToRestore) {
        // No search, restoring state
        this.restoreExpansionState(this.treeNodes, expandedIdsToRestore);
      } else {
        // No search, initial load: Expand only root collections
        this.treeNodes.forEach((node) => {
          if (node.type === 'collection' && node.level === 0) {
            node.expanded = true;
          }
        });
      }

      this.buildNodeMap(this.treeNodes);

      // <<< START SELECTION RECONCILIATION >>>
      const visibleArticleIds = this.getVisibleArticleIds(this.treeNodes);
      const currentSelected = new Set(this.selectedArticleIds);
      let changed = false;
      currentSelected.forEach((id) => {
        if (!visibleArticleIds.has(id)) {
          currentSelected.delete(id);
          changed = true;
        }
      });
      if (changed) {
        this.selectedArticleIds = currentSelected;
      }
      // <<< END SELECTION RECONCILIATION >>>
    } catch (error) {
      console.error('Error loading hierarchy data:', error);
      await this.msgSrvc.showError(error);
      this.treeNodes = [];
      this.allCollections = [];
      this.currentArticles = [];
    } finally {
      this.isLoading = false;
      this.cdRef.markForCheck();
    }
  }

  private buildTree(
    collections: KnowledgeBaseCollection[],
    articles: Partial<KnowledgeBaseArticle>[], // Already filtered articles
    parentId: string | null,
    level: number,
    searchTerm: string,
  ): HierarchyTreeNode[] {
    const isSearching = searchTerm && searchTerm.length > 0;
    let nodes: HierarchyTreeNode[] = [];

    const childCollectionsData = collections.filter((coll) => coll.parentId === parentId);

    // Build child collection nodes recursively first
    let childCollectionNodes = childCollectionsData
      .map((coll): HierarchyTreeNode | null => {
        const children = this.buildTree(collections, articles, coll.id, level + 1, searchTerm);

        // Pruning Logic: Only prune if searching AND the initial fetch found articles
        if (isSearching && articles.length > 0) {
          const hasDirectArticles = articles.some((a) => a.collectionId === coll.id);
          const hasChildContent = children.length > 0;
          if (!hasDirectArticles && !hasChildContent) {
            return null; // Prune
          }
        }
        // If not searching, or if search returned 0 articles initially, don't prune based on content

        return {
          id: coll.id,
          name: coll.name,
          type: 'collection',
          parentId: coll.parentId,
          level: level,
          expanded: false, // Let loadInitialData handle final expansion state
          children: children,
          data: coll,
        };
      })
      .filter((node): node is HierarchyTreeNode => node !== null);

    // Add nullish coalescing for sorting
    childCollectionNodes.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

    // Get articles for the current parent
    const childArticles = articles
      .filter((article) => article.collectionId === parentId && article.id)
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))
      .map(
        (article): HierarchyTreeNode => ({
          id: article.id!,
          name: article.title ?? 'Untitled Article',
          type: 'article',
          parentId: article.collectionId,
          level: level,
          updatedAt: article.updatedAt,
          tags: (article as any).tags ?? [],
          data: article as KnowledgeBaseArticle,
        }),
      );

    // Assemble nodes
    if (parentId === null) {
      // Only add create action rows if NOT searching
      if (!isSearching) {
        const createCollectionActionNode: HierarchyTreeNode = {
          id: '--create-collection--',
          name: 'Create Collection Action',
          type: 'create-action',
          level: 0,
          parentId: null,
        };
        // Add a new node type for creating root articles
        const createRootArticleActionNode: HierarchyTreeNode = {
          id: '--create-root-article--',
          name: 'Create Root Article Action',
          type: 'create-root-article-action', // New type
          level: 0,
          parentId: null,
        };
        // Order: Collections, Create Collection, Articles, Create Article
        nodes = [
          ...childCollectionNodes,
          createCollectionActionNode,
          ...childArticles,
          createRootArticleActionNode, // Add new node at the end
        ];
      } else {
        // If searching, omit create action rows
        nodes = [...childCollectionNodes, ...childArticles];
      }
    } else {
      nodes = [...childCollectionNodes, ...childArticles];
    }

    return nodes;
  }

  // Helper to populate the node map for efficient lookup
  private buildNodeMap(nodes: HierarchyTreeNode[]): void {
    for (const node of nodes) {
      this.nodeMap.set(node.id, node);
      if (node.children) {
        this.buildNodeMap(node.children);
      }
    }
  }

  toggleExpand(node: HierarchyTreeNode, event: MouseEvent): void {
    if (node.type === 'collection') {
      node.expanded = !node.expanded;
      this.cdRef.markForCheck();
    }
  }

  selectNode(node: HierarchyTreeNode, event?: MouseEvent): void {
    if (node.type === 'create-action' || node.type === 'collection') {
      return;
    }

    if (node.type === 'article') {
      // Check for modifier keys (Cmd/Ctrl/Shift) or middle click
      const openInNewContext = event && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

      if (openInNewContext) {
        // Construct the URL for the article edit page
        const url = this.router.createUrlTree(['/dashboard/knowledge/articles', node.id, 'edit']).toString();
        window.open(url, '_blank');
        // Prevent default browser action only for middle-click
        if (event.button === 1) {
          event.preventDefault();
        }
        // Note: We don't preventDefault for Shift+Click as the browser handles the new window action.
      } else {
        // Standard left-click: navigate in the current tab
        this.selectedNode = node;
        this.router.navigate(['/dashboard/knowledge/articles', node.id, 'edit']);
      }
    }
  }

  async handleCreateActionClick(event: MouseEvent): Promise<void> {
    // We pass null for the parent node to indicate root level creation
    // Pass the original mouse event if needed by newCollection
    await this.newCollection(null, event);
  }

  // --- Drag & Drop Handlers (Single Implementation) ---

  onDragStart(event: DragEvent, node: HierarchyTreeNode): void {
    if (!event.dataTransfer) return;
    (event.target as HTMLElement).classList.add('dragging');
    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({ id: node.id, type: node.type, parentId: node.parentId }),
    );
    event.dataTransfer.effectAllowed = 'move';
  }

  onDragEnd(event: DragEvent): void {
    (event.target as HTMLElement).classList.remove('dragging');
  }

  onDragOver(event: DragEvent, targetNode?: HierarchyTreeNode): void {
    event.preventDefault();
    if (!event.dataTransfer) return;

    let effectiveHighlightTargetElement: HTMLElement | null = null;
    let isValidDrop = false;

    if (!targetNode) {
      // Hovering ROOT ZONE directly
      isValidDrop = true;
      effectiveHighlightTargetElement = (event.currentTarget as HTMLElement).closest('.root-drop-zone');
    } else if (targetNode.type === 'collection') {
      // Hovering COLLECTION
      isValidDrop = true;
      effectiveHighlightTargetElement = event.currentTarget as HTMLElement;
    } else if (targetNode.type === 'article') {
      // Hovering ARTICLE
      if (targetNode.parentId) {
        // Article IN A COLLECTION
        const parentElement = document.getElementById('node-' + targetNode.parentId);
        if (parentElement) {
          isValidDrop = true;
          effectiveHighlightTargetElement = parentElement; // Highlight PARENT
        } else {
          isValidDrop = false;
        }
      } else {
        // Article AT ROOT LEVEL
        isValidDrop = true;
        // Highlight the entire root drop zone instead of the specific article
        effectiveHighlightTargetElement = (event.currentTarget as HTMLElement).closest('.root-drop-zone');
      }
    } else {
      // Hovering CREATE ARTICLE/ACTION
      isValidDrop = false;
    }

    // --- Apply / Clear Styles ONLY if effective target changed ---
    if (this.currentlyHighlightedDropTarget !== effectiveHighlightTargetElement) {
      // Remove highlight from the previous target
      if (this.currentlyHighlightedDropTarget) {
        this.removeDragOverStyle(this.currentlyHighlightedDropTarget);
      }
      // Add highlight to the new target if it's valid
      if (effectiveHighlightTargetElement && isValidDrop) {
        this.addDragOverStyle(effectiveHighlightTargetElement);
        this.currentlyHighlightedDropTarget = effectiveHighlightTargetElement;
      } else {
        // If new target isn't valid or doesn't exist, clear tracking
        this.currentlyHighlightedDropTarget = null;
      }
    }

    // --- Set Drop Effect ---
    event.dataTransfer.dropEffect = isValidDrop ? 'move' : 'none';

    // --- Stop Bubbling ---
    // Stop bubbling for clearly invalid leaf targets (like create-action or articles)
    // Exception: Allow bubbling from root articles so root zone can catch it
    if (!isValidDrop && targetNode && targetNode.type !== 'collection' && targetNode.parentId) {
      // Added check for parentId
      event.stopPropagation();
    }
  }

  onDragLeave(event: DragEvent): void {
    if (this.currentlyHighlightedDropTarget) {
      this.removeDragOverStyle(this.currentlyHighlightedDropTarget);
      this.currentlyHighlightedDropTarget = null;
    }
  }

  async onDrop(event: DragEvent, targetNode?: HierarchyTreeNode): Promise<void> {
    event.preventDefault();
    // Clear highlight
    if (this.currentlyHighlightedDropTarget) {
      this.removeDragOverStyle(this.currentlyHighlightedDropTarget);
      this.currentlyHighlightedDropTarget = null;
    }

    const draggedData = this.getDraggedData(event);
    if (!draggedData) return;

    // Determine ACTUAL target collection ID
    let actualTargetCollectionId: string | null = null;
    if (!targetNode) {
      actualTargetCollectionId = null;
    } else if (targetNode.type === 'collection') {
      actualTargetCollectionId = targetNode.id;
    } else if (targetNode.type === 'article') {
      actualTargetCollectionId = targetNode.parentId ?? null;
    }

    const { id: draggedItemId, type: draggedItemType, parentId: currentParentId } = draggedData;

    // --- Validation ---
    if (draggedItemId === targetNode?.id) return; // Dropping item onto itself (e.g. article)
    if ((currentParentId ?? null) === actualTargetCollectionId) return; // Dropping into same parent
    if (draggedItemType === 'collection' && actualTargetCollectionId) {
      if (this.isDescendant(actualTargetCollectionId, draggedItemId)) {
        await this.msgSrvc.showError('Cannot move a collection into one of its sub-collections.');
        return;
      }
    }

    // --- Perform Move ---
    this.isMovingItem = true;
    this.cdRef.markForCheck();
    try {
      let updatedItem: KnowledgeBaseCollection | Partial<KnowledgeBaseArticle>;

      // 1. Call API
      if (draggedItemType === 'collection') {
        updatedItem = await this.collectionsService.update(draggedItemId, { parentId: actualTargetCollectionId });
      } else {
        // Article
        updatedItem = await this.articlesService.updatePartial(draggedItemId, {
          collectionId: actualTargetCollectionId,
        });
      }

      // 2. Update Local Tree Structure using helper
      await this.applyLocalUpdate(draggedItemId, { updatedAt: updatedItem.updatedAt }, actualTargetCollectionId);

      await this.msgSrvc.show('Item moved successfully!');
    } catch (error) {
      console.error('[Drop] Failed to move item:', error);
      await this.msgSrvc.showError(error);
      await this.loadInitialData();
    } finally {
      this.isMovingItem = false;
      this.cdRef.markForCheck();
    }
  }

  // --- Helper Methods (Single Implementation) ---

  private getDraggedData(
    event: DragEvent,
  ): { id: string; type: 'collection' | 'article'; parentId?: string | null } | null {
    if (!event.dataTransfer) return null;
    try {
      const data = event.dataTransfer.getData('application/json');
      if (!data) {
        return null;
      }
      return JSON.parse(data);
    } catch (e) {
      return null;
    }
  }

  private addDragOverStyle(element: HTMLElement): void {
    element.classList.add('drag-over-target');
  }

  private removeDragOverStyle(element: HTMLElement): void {
    element.classList.remove('drag-over-target');
  }

  // Optimized helper using the nodeMap
  private isDescendant(potentialChildId: string, potentialAncestorId: string): boolean {
    let current = this.nodeMap.get(potentialChildId);
    while (current && current.parentId) {
      // Iterate up the tree using parentId
      if (current.parentId === potentialAncestorId) {
        return true;
      }
      current = this.nodeMap.get(current.parentId);
    }
    return false;
  }

  // --- Context Menu Implementation ---

  async showContextMenu(event: MouseEvent, node: HierarchyTreeNode | null): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    let options: ActionDropdownType[] = [];
    const NEW_EDIT_ARTICLE_LABEL = 'Edit Article';

    if (node) {
      if (node.type === 'article') {
        options.push({ label: NEW_EDIT_ARTICLE_LABEL, icon: 'pencil-outline' });
      }

      options.push({ label: ACTION_LABELS.EDIT, icon: 'create-outline' });
      options.push({ label: ACTION_LABELS.MOVE, icon: 'swap-horizontal-outline' });
      if (node.type === 'collection') {
        options.push({ label: ACTION_LABELS.NEW_ARTICLE, icon: 'add-outline' });
        options.push({ label: ACTION_LABELS.NEW_COLLECTION, icon: 'add-circle-outline' });
      }
      options.push({ label: ACTION_LABELS.DELETE, icon: 'trash-outline', destructive: true });
    } else {
      options.push({ label: ACTION_LABELS.NEW_ARTICLE, icon: 'add-outline' });
      options.push({ label: ACTION_LABELS.NEW_COLLECTION, icon: 'add-circle-outline' });
    }

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event: event,
      showBackdrop: false,
      cssClass: 'context-menu-popover',
      componentProps: {
        options: options,
        callback: async (index: number, label: string, id?: string, metadata?: any) => {
          popover.dismiss(); // Dismiss the popover first

          // Handle selected action directly within the callback
          switch (label) {
            case NEW_EDIT_ARTICLE_LABEL:
              if (node) this.selectNode(node);
              break;
            case ACTION_LABELS.EDIT:
              if (node) await this.editNode(node);
              break;
            case ACTION_LABELS.MOVE:
              if (node) await this.moveNode(node, event);
              break;
            case ACTION_LABELS.DELETE:
              if (node) await this.deleteNode(node);
              break;
            case ACTION_LABELS.NEW_ARTICLE:
              await this.newArticle(node?.type === 'collection' ? node : null, event);
              break;
            case ACTION_LABELS.NEW_COLLECTION:
              await this.newCollection(node?.type === 'collection' ? node : null, event);
              break;
            default:
          }
        },
      },
    });
    await popover.present();
  }

  // --- Action Handler Placeholders ---

  async editNode(node: HierarchyTreeNode): Promise<void> {
    if (node.type === 'collection') {
      // Use upsert collection modal for editing collections
      const modal = await this.modalCtrl.create({
        component: UpsertCollectionModalComponent,
        componentProps: {
          collection: node.data as KnowledgeBaseCollection,
        },
      });

      await modal.present();
      const { data } = await modal.onWillDismiss();

      if (data?.success) {
        // Refresh the data to show updated collection
        const expandedIds = this.getExpandedCollectionIds(this.treeNodes);
        await this.loadInitialData(expandedIds);
      }
    } else {
      // For articles, keep the existing alert behavior for now
      const alert = await this.alertCtrl.create({
        header: `Edit ${node.type}`,
        inputs: [
          {
            name: 'newName',
            type: 'text',
            value: node.name,
            placeholder: 'Article Title',
          },
        ],
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Save',
            handler: async (data) => {
              const newName = data.newName?.trim();
              if (!newName || newName === node.name) return;

              this.isLoading = true;
              this.cdRef.markForCheck();
              try {
                // 1. Call API
                const updatedItem = await this.articlesService.updatePartial(node.id, { title: newName });

                // 2. Update Local Tree Structure using helper
                await this.applyLocalUpdate(node.id, {
                  name: newName,
                  updatedAt: updatedItem?.updatedAt,
                });

                this.msgSrvc.show('Item updated.');
              } catch (e) {
                console.error('Edit failed:', e);
                this.msgSrvc.showError(e);
                // Fallback refresh on error
                const expandedIds = this.getExpandedCollectionIds(this.treeNodes);
                await this.loadInitialData(expandedIds);
              } finally {
                this.isLoading = false;
                this.cdRef.markForCheck();
              }
            },
          },
        ],
      });
      await alert.present();

      // Optional: Focus input after render
      setTimeout(() => {
        const inputElement = document.querySelector('ion-alert input[name="newName"]') as HTMLInputElement | null;
        inputElement?.focus();
        inputElement?.select();
      }, 150);
    }
  }

  async moveNode(node: HierarchyTreeNode, triggerEvent: MouseEvent): Promise<void> {
    const popover = await this.popoverCtrl.create({
      component: CollectionSelectorComponent,
      cssClass: 'collection-selector-popover',
      backdropDismiss: true,
      componentProps: {
        currentCollectionId: node.parentId ?? null,
        maxHeight: '60vh',
        callback: async (selectedId: string | null) => {
          const targetCollectionId = selectedId;
          // Validate move (already in target?, circular?)
          if ((node.parentId ?? null) === targetCollectionId) return; // No change
          if (node.type === 'collection' && this.isDescendant(selectedId!, node.id)) {
            await this.msgSrvc.showError('Cannot move a collection into one of its sub-collections.');
            return;
          }

          // Perform move
          this.isMovingItem = true;
          this.cdRef.markForCheck();
          try {
            let updatedItem: KnowledgeBaseCollection | Partial<KnowledgeBaseArticle>;
            // 1. Call API
            if (node.type === 'collection') {
              updatedItem = await this.collectionsService.update(node.id, {
                parentId: targetCollectionId,
              });
            } else {
              // Article
              updatedItem = await this.articlesService.updatePartial(node.id, {
                collectionId: targetCollectionId,
              });
            }

            // 2. Update Local Tree Structure using helper
            await this.applyLocalUpdate(node.id, { updatedAt: updatedItem.updatedAt }, targetCollectionId);

            this.msgSrvc.show('Item moved.');
          } catch (e) {
            console.error('Move failed:', e);
            this.msgSrvc.showError('Move failed.');
          } finally {
            this.isMovingItem = false;
            this.cdRef.markForCheck();
          }
        },
      },
    });
    await popover.present();
  }

  async deleteNode(node: HierarchyTreeNode): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: `Delete ${node.type}?`,
      message: `Are you sure you want to delete "${node.name}"? This cannot be undone.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            this.isMovingItem = true;
            this.cdRef.markForCheck();
            try {
              if (node.type === 'collection') {
                await this.collectionsService.delete(node.id);
              } else {
                await this.articlesService.delete(node.id);
              }
              this.msgSrvc.show(`${node.type.charAt(0).toUpperCase() + node.type.slice(1)} deleted.`);
              const expandedIds = this.getExpandedCollectionIds(this.treeNodes);
              await this.loadInitialData(expandedIds); // Refresh
            } catch (e) {
              console.error('Delete failed:', e);
              this.msgSrvc.showError(e);
            } finally {
              this.isMovingItem = false;
              this.cdRef.markForCheck();
            }
          },
        },
      ],
    });
    await alert.present();
  }

  async newArticle(parentCollectionNode: HierarchyTreeNode | null, triggerEvent?: MouseEvent): Promise<void> {
    const parentId = parentCollectionNode?.id ?? null;
    this.router.navigate(['/dashboard/knowledge/articles/new'], { queryParams: { collectionId: parentId } });
  }

  async newCollection(parentCollectionNode: HierarchyTreeNode | null, triggerEvent?: MouseEvent): Promise<void> {
    const parentId = parentCollectionNode?.id ?? null;
    const modal = await this.modalCtrl.create({
      component: UpsertCollectionModalComponent,
      componentProps: {
        parentId: parentId,
        projectId: this.authService.selectedProject?.id ?? null,
      },
    });

    modal.onDidDismiss().then(async (result) => {
      if (result.data && result.data.success) {
        this.msgSrvc.show('Collection created.');
        const expandedIds = this.getExpandedCollectionIds(this.treeNodes);
        await this.loadInitialData(expandedIds); // Refresh
      }
    });

    await modal.present();
  }

  // Helper to get IDs of currently expanded collections
  private getExpandedCollectionIds(nodes: HierarchyTreeNode[] = this.treeNodes): Set<string> {
    const expandedIds = new Set<string>();
    const traverse = (currentNodes: HierarchyTreeNode[]) => {
      for (const node of currentNodes) {
        if (node.type === 'collection' && node.expanded && node.id) {
          expandedIds.add(node.id);
          if (node.children) {
            traverse(node.children);
          }
        }
      }
    };
    traverse(nodes);
    return expandedIds;
  }

  private restoreExpansionState(nodes: HierarchyTreeNode[], expandedIds: Set<string>): void {
    const traverse = (currentNodes: HierarchyTreeNode[]) => {
      for (const node of currentNodes) {
        if (node.type === 'collection') {
          if (expandedIds.has(node.id)) {
            node.expanded = true;
            if (node.children) {
              traverse(node.children);
            }
          } else {
            node.expanded = false;
          }
        }
      }
    };
    traverse(nodes);
  }

  // Helper to recursively update node levels
  private updateNodeLevels(node: HierarchyTreeNode, newLevel: number): void {
    node.level = newLevel;
    if (node.children) {
      node.children.forEach((child) => this.updateNodeLevels(child, newLevel + 1));
    }
  }

  // Helper to apply local updates (move/rename) to the tree structure
  private async applyLocalUpdate(
    nodeId: string,
    updates: Partial<HierarchyTreeNode>,
    newParentId?: string | null,
  ): Promise<void> {
    const node = this.nodeMap.get(nodeId);
    if (!node) {
      console.error(`[applyLocalUpdate] Node not found in map: ${nodeId}`);
      await this.loadInitialData();
      return;
    }

    const currentParentId = node.parentId ?? null;
    const parentChanged = newParentId !== undefined && newParentId !== currentParentId;

    let oldSiblingsArray: HierarchyTreeNode[] | null = null;
    if (parentChanged) {
      // Find old siblings array
      const oldParentNode = this.nodeMap.get(currentParentId ?? '') ?? null;
      if (oldParentNode && oldParentNode.children) {
        oldSiblingsArray = oldParentNode.children;
      } else {
        oldSiblingsArray = this.treeNodes; // Root level
      }
      // Remove from old siblings
      const oldIndex = oldSiblingsArray.findIndex((n) => n.id === nodeId);
      if (oldIndex > -1) {
        oldSiblingsArray.splice(oldIndex, 1);
      }
    }

    // Apply property updates to the node
    Object.assign(node, updates);
    if (parentChanged) {
      node.parentId = newParentId;
    }

    // Find new siblings array & add/update position
    const finalParentId = parentChanged ? newParentId : currentParentId;
    const parentNode = this.nodeMap.get(finalParentId ?? '') ?? null;
    let siblingsArray: HierarchyTreeNode[];
    if (parentNode && parentNode.children) {
      siblingsArray = parentNode.children;
    } else {
      siblingsArray = this.treeNodes; // Root level
    }

    // Update level if parent changed
    if (parentChanged) {
      const newLevel = parentNode ? parentNode.level + 1 : 0;
      this.updateNodeLevels(node, newLevel);
    }

    // Add node back if it was moved
    if (parentChanged) {
      if (finalParentId === null) {
        // Adding to root
        const createActionIndex = siblingsArray.findIndex((n) => n.type === 'create-action');
        if (createActionIndex > -1) {
          siblingsArray.splice(createActionIndex, 0, node);
        } else {
          siblingsArray.push(node);
        }
      } else {
        // Adding to a collection
        siblingsArray.push(node);
      }
    }

    // Re-sort the siblings array(s)
    const sortNodes = (a: HierarchyTreeNode, b: HierarchyTreeNode) => {
      const typeOrder = {
        collection: 1,
        'create-action': 2, // Create Collection
        article: 3,
        'create-root-article-action': 4, // Create Article (Root)
      };
      const orderA = typeOrder[a.type] ?? 99;
      const orderB = typeOrder[b.type] ?? 99;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      // Add nullish coalescing for sorting articles by name (title)
      if ((a.type === 'article' || a.type === 'collection') && (b.type === 'article' || b.type === 'collection')) {
        return (a.name ?? '').localeCompare(b.name ?? '');
      }
      return 0; // Keep relative order for other types (e.g., action rows)
    };

    siblingsArray.sort(sortNodes);
    // Re-sort old siblings if they were affected
    if (parentChanged && oldSiblingsArray && oldSiblingsArray !== siblingsArray) {
      oldSiblingsArray.sort(sortNodes);
    }

    // Trigger change detection
    this.cdRef.markForCheck();
  }

  // Add expandAllNodes helper
  private expandAllNodes(nodes: HierarchyTreeNode[]): void {
    for (const node of nodes) {
      if (node.type === 'collection') {
        node.expanded = true;
        if (node.children) {
          this.expandAllNodes(node.children);
        }
      }
    }
  }

  // Method to handle checkbox changes
  toggleArticleSelection(node: HierarchyTreeNode, event: any): void {
    event.stopPropagation();
    const isChecked = event.detail?.checked;
    if (isChecked) {
      this.selectedArticleIds.add(node.id);
    } else {
      this.selectedArticleIds.delete(node.id);
    }
    this.cdRef.markForCheck();
  }

  // Unified click handler
  handleNodeClick(node: HierarchyTreeNode, event: MouseEvent): void {
    if (node.type === 'collection') {
      this.toggleExpand(node, event);
    } else if (node.type === 'article') {
      this.selectNode(node, event);
    }
  }

  // <<< ADD HELPER FUNCTION >>>
  private getVisibleArticleIds(nodes: HierarchyTreeNode[]): Set<string> {
    const visibleIds = new Set<string>();
    const traverse = (currentNodes: HierarchyTreeNode[]) => {
      for (const node of currentNodes) {
        if (node.type === 'article') {
          visibleIds.add(node.id);
        }
        if (node.children) {
          traverse(node.children);
        }
      }
    };
    traverse(nodes);
    return visibleIds;
  }

  // --- Bulk Action Methods ---

  deselectAllArticles(): void {
    this.selectedArticleIds.clear();
    this.cdRef.markForCheck();
  }

  selectAllArticles(): void {
    this.currentArticles.forEach((a) => this.selectedArticleIds.add(a.id));
    this.cdRef.markForCheck();
  }

  async bulkMoveArticles(triggerEvent: MouseEvent): Promise<void> {
    const articleIds = Array.from(this.selectedArticleIds);
    if (articleIds.length === 0) return;

    const popover = await this.popoverCtrl.create({
      component: CollectionSelectorComponent,
      cssClass: 'collection-selector-popover',
      backdropDismiss: true,
      componentProps: {
        maxHeight: '60vh',
        callback: async (selectedId: string | null) => {
          const targetCollectionId = selectedId;

          // Optional: Add confirmation step
          const targetName = targetCollectionId
            ? this.nodeMap.get(targetCollectionId)?.name ?? 'Selected Collection'
            : 'Knowledge Base Root';
          const confirmAlert = await this.alertCtrl.create({
            header: 'Confirm Move',
            message: `Move ${articleIds.length} article${articleIds.length > 1 ? 's' : ''} to "${targetName}"?`,
            buttons: [
              { text: 'Cancel', role: 'cancel' },
              {
                text: 'Move',
                handler: async () => {
                  // 3. Call the backend service (assuming it exists)
                  this.isLoading = true;
                  this.cdRef.markForCheck();
                  try {
                    // Assumes articlesService.bulkMove exists
                    await this.articlesService.bulkMove(articleIds, targetCollectionId);
                    this.msgSrvc.show('Articles moved successfully.');
                    this.deselectAllArticles(); // Clear selection
                    // Refresh data - retain expansion state
                    const expandedIds = this.getExpandedCollectionIds();
                    await this.loadInitialData(expandedIds);
                  } catch (e) {
                    console.error('Bulk move failed:', e);
                    this.msgSrvc.showError(e);
                    // Optionally reload even on error to ensure consistency
                    // const expandedIds = this.getExpandedCollectionIds();
                    // await this.loadInitialData(expandedIds);
                  } finally {
                    this.isLoading = false;
                    this.cdRef.markForCheck();
                  }
                },
              },
            ],
          });
          await confirmAlert.present();
        },
      },
    });
    await popover.present();
  }

  async bulkDeleteArticles(): Promise<void> {
    const articleIds = Array.from(this.selectedArticleIds);
    if (articleIds.length === 0) return;

    const alert = await this.alertCtrl.create({
      header: `Delete ${articleIds.length} Article${articleIds.length > 1 ? 's' : ''}?`,
      message: 'Are you sure? This cannot be undone.',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            this.isLoading = true; // Use isLoading to potentially show global spinner
            this.cdRef.markForCheck();
            try {
              // Assumes articlesService.bulkDelete exists
              await this.articlesService.bulkDelete(articleIds);
              this.msgSrvc.show(`${articleIds.length} article${articleIds.length > 1 ? 's' : ''} deleted.`);
              this.deselectAllArticles(); // Clear selection
              // Refresh data - retain expansion state
              const expandedIds = this.getExpandedCollectionIds();
              await this.loadInitialData(expandedIds);
            } catch (e) {
              console.error('Bulk delete failed:', e);
              this.msgSrvc.showError(e);
            } finally {
              this.isLoading = false;
              this.cdRef.markForCheck();
            }
          },
        },
      ],
    });
    await alert.present();
  }

  createArticle() {
    this.router.navigate(['./new'], { relativeTo: this.route });
  }

  openRunQueryModal() {
    this.showRunQueryModal = true;
  }

  onDismissRunQueryModal() {
    this.showRunQueryModal = false;
  }

  createRootCollection() {
    this.newCollection(null);
  }

  async openBulkManageGroupsModal(): Promise<void> {
    const articleIds = Array.from(this.selectedArticleIds);
    if (articleIds.length === 0) return;

    try {
      // Fetch required data in parallel
      const [selectedArticlesData, allUserGroupsResponse] = await Promise.all([
        this.articlesService.getArticlesByIds(articleIds, ['id', 'userGroupIds']),
        this.userGroupsService.list({
          limit: null,
          order: ['name'],
          descending: false,
        }),
      ]);

      // Filter out groups without an ID to satisfy the UserGroup[] type
      const allUserGroups: UserGroup[] = (allUserGroupsResponse.data ?? []).filter((g): g is UserGroup => !!g.id);

      const modal = await this.modalCtrl.create({
        component: BulkManageGroupsComponent,
        componentProps: {
          articles: selectedArticlesData,
          availableGroups: allUserGroups,
        },
        cssClass: 'modal-sm',
      });

      await modal.present();

      const { data, role } = await modal.onDidDismiss();

      if (role === 'apply' && data) {
        try {
          await this.articlesService.bulkSetUserGroups(data.articleIds, data.finalGroupIds);
          await this.msgSrvc.show(`User groups updated for ${data.articleIds.length} article(s).`);
          this.deselectAllArticles();

          const userGroupFilterOption = this.filterOptions.find((opt) => opt.id === 'userGroupId');
          const isUserGroupFilterActive = userGroupFilterOption && this.filters.has(userGroupFilterOption);

          if (isUserGroupFilterActive) {
            // Refresh needed if filtering by group
            const expandedIds = this.getExpandedCollectionIds();
            await this.loadInitialData(expandedIds);
          }
        } catch (error) {
          console.error('Bulk set user groups failed:', error);
          await this.msgSrvc.showError(error);
          const expandedIds = this.getExpandedCollectionIds();
          await this.loadInitialData(expandedIds);
        } finally {
          this.cdRef.markForCheck();
        }
      } // else role is 'cancel' or data is null - do nothing
    } catch (error) {
      console.error('Error preparing bulk manage groups modal:', error);
      await this.msgSrvc.showError(error);
    }
  }

  async openBulkManageChannelsModal(): Promise<void> {
    const articleIds = Array.from(this.selectedArticleIds);
    if (articleIds.length === 0) return;

    try {
      // Fetch required data in parallel
      const [selectedArticlesData, allChannelsResponse] = await Promise.all([
        this.articlesService.getArticlesByIds(articleIds, ['id', 'channelIds']),
        this.customersService.list({
          limit: null,
          order: ['name'],
          descending: false,
        }),
      ]);

      // Filter out groups without an ID to satisfy the UserGroup[] type
      const allChannels: Customer[] = (allChannelsResponse.data ?? []).filter((c): c is Customer => !!c.id);

      const modal = await this.modalCtrl.create({
        component: BulkManageChannelsComponent,
        componentProps: {
          articles: selectedArticlesData,
          availableChannels: allChannels,
        },
        cssClass: 'modal-sm',
      });

      await modal.present();

      const { data, role } = await modal.onDidDismiss();

      if (role === 'apply' && data) {
        try {
          await this.articlesService.bulkSetChannels(data.articleIds, data.finalChannelIds);
          await this.msgSrvc.show(`Channels updated for ${data.articleIds.length} article(s).`);
          this.deselectAllArticles();

          const channelFilterOption = this.filterOptions.find((opt) => opt.id === 'channelId');
          const isChannelFilterActive = channelFilterOption && this.filters.has(channelFilterOption);

          if (isChannelFilterActive) {
            // Refresh needed if filtering by group
            const expandedIds = this.getExpandedCollectionIds();
            await this.loadInitialData(expandedIds);
          }
        } catch (error) {
          console.error('Bulk set user groups failed:', error);
          await this.msgSrvc.showError(error);
          const expandedIds = this.getExpandedCollectionIds();
          await this.loadInitialData(expandedIds);
        } finally {
          this.cdRef.markForCheck();
        }
      } // else role is 'cancel' or data is null - do nothing
    } catch (error) {
      console.error('Error preparing bulk manage channels modal:', error);
      await this.msgSrvc.showError(error);
    }
  }

  async addTagToArticle(node: HierarchyTreeNode, event: MouseEvent): Promise<void> {
    if (node.type !== 'article') return;
    const articleId = node.id;
    const existingTagIds = new Set(node.tags?.map((t) => t.id) ?? []);

    this.searchablePopoverService.tags({
      event,
      callback: async (tagId: string, tagName: string, selectedTag: Tag) => {
        if (!tagId || !selectedTag) return;

        // Prevent adding if already present locally
        if (existingTagIds.has(tagId)) return;

        try {
          await this.tagsService.createKnowledgeBaseArticleLinks(tagId, [articleId]);
          node.tags = [...(node.tags ?? []), { id: tagId, name: tagName }];
          this.cdRef.markForCheck(); // Keep this one after local data update
          this.msgSrvc.show(`Tag '${tagName}' added.`);
        } catch (error) {
          console.error('Error adding tag link:', error);
          this.msgSrvc.showError('Failed to add tag.');
        }
      },
    });
  }

  async showTagOptions(node: HierarchyTreeNode, tag: { id: string; name: string }, event: MouseEvent): Promise<void> {
    if (node.type !== 'article') return;
    const articleId = node.id;

    const options: ActionDropdownType[] = [
      {
        label: `Remove tag "${tag.name}"`,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event: event,
      showBackdrop: false,
      cssClass: 'context-menu-popover',
      componentProps: {
        options: options,
        callback: async (index: number, label: string) => {
          popover.dismiss();
          if (label.startsWith('Remove tag')) {
            try {
              await this.tagsService.deleteKnowledgeBaseArticleLinks(tag.id, [articleId]);
              node.tags = node.tags?.filter((t) => t.id !== tag.id);
              this.cdRef.markForCheck(); // Keep this one after local data update
              this.msgSrvc.show(`Tag '${tag.name}' removed.`);
            } catch (error) {
              console.error('Error removing tag link:', error);
              this.msgSrvc.showError('Failed to remove tag.');
            }
          }
        },
      },
    });
    await popover.present();
  }

  // --- Bulk Tag Management ---

  async bulkAddTag($event: MouseEvent): Promise<void> {
    const articleIds = Array.from(this.selectedArticleIds);
    if (articleIds.length === 0) return;

    this.searchablePopoverService.tags({
      event: undefined,
      callback: async (tagId: string, tagName: string, selectedTag: Tag) => {
        if (!tagId || !selectedTag) return;

        const confirmAlert = await this.alertCtrl.create({
          header: 'Confirm Add Tag',
          message: `Add the tag "${tagName}" to ${articleIds.length} selected article${articleIds.length > 1 ? 's' : ''}?`,
          buttons: [
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Add Tag',
              handler: async () => {
                this.isLoading = true;
                this.cdRef.markForCheck();
                try {
                  await this.tagsService.createKnowledgeBaseArticleLinks(tagId, articleIds);
                  this.msgSrvc.show(`Tag "${tagName}" added to ${articleIds.length} article(s).`);
                  this.deselectAllArticles();
                  // Refresh data to show updated tags
                  const expandedIds = this.getExpandedCollectionIds();
                  await this.loadInitialData(expandedIds);
                } catch (error) {
                  console.error('Bulk add tag failed:', error);
                  this.msgSrvc.showError(error);
                  // Optionally reload even on error
                  const expandedIds = this.getExpandedCollectionIds();
                  await this.loadInitialData(expandedIds);
                } finally {
                  this.isLoading = false;
                  this.cdRef.markForCheck();
                }
              },
            },
          ],
        });
        await confirmAlert.present();
      },
    });
  }

  async bulkRemoveTag($event: MouseEvent): Promise<void> {
    const articleIds = Array.from(this.selectedArticleIds);
    if (articleIds.length === 0) return;

    this.searchablePopoverService.tags({
      event: undefined,
      callback: async (tagId: string, tagName: string, selectedTag: Tag) => {
        if (!tagId || !selectedTag) return;

        const confirmAlert = await this.alertCtrl.create({
          header: 'Confirm Remove Tag',
          message: `Remove the tag "${tagName}" from ${articleIds.length} selected article${articleIds.length > 1 ? 's' : ''}?`,
          buttons: [
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Remove Tag',
              role: 'destructive', // Indicate a potentially destructive action
              handler: async () => {
                this.isLoading = true;
                this.cdRef.markForCheck();
                try {
                  await this.tagsService.deleteKnowledgeBaseArticleLinks(tagId, articleIds);
                  this.msgSrvc.show(`Tag "${tagName}" removed from ${articleIds.length} article(s).`);
                  this.deselectAllArticles();
                  // Refresh data to show updated tags
                  const expandedIds = this.getExpandedCollectionIds();
                  await this.loadInitialData(expandedIds);
                } catch (error) {
                  console.error('Bulk remove tag failed:', error);
                  this.msgSrvc.showError(error);
                  // Optionally reload even on error
                  const expandedIds = this.getExpandedCollectionIds();
                  await this.loadInitialData(expandedIds);
                } finally {
                  this.isLoading = false;
                  this.cdRef.markForCheck();
                }
              },
            },
          ],
        });
        await confirmAlert.present();
      },
    });
  }

  // --- Document Import Methods ---

  showImportDocumentOptions(event: MouseEvent): void {
    this.showImportModal = true;
    this.selectedFile = null;
    this.importOptions = {
      title: '',
      collectionId: null,
      extractImages: true,
      strategy: 'auto',
      vlmModelProvider: 'anthropic',
    };
    this.cdRef.markForCheck();
  }

  onDismissImportModal(): void {
    this.showImportModal = false;
    this.isUploading = false;
    this.selectedFile = null;
    this.cdRef.markForCheck();
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    // Validate file size (100MB max)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      await this.msgSrvc.showError('File size exceeds 100MB limit');
      input.value = ''; // Reset input
      return;
    }

    // Store the file for later import
    this.selectedFile = file;
    this.cdRef.markForCheck();
  }

  async confirmImport(): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    this.isUploading = true;
    this.cdRef.markForCheck();

    try {
      const result = await this.articlesService.importDocument(this.selectedFile, {
        title: this.importOptions.title || undefined,
        projectId: this.authService.selectedProject?.id ?? undefined,
        collectionId: this.importOptions.collectionId ?? undefined,
        extractImages: this.importOptions.extractImages,
        strategy: this.importOptions.strategy,
        vlmModelProvider: this.importOptions.strategy === 'vlm' ? this.importOptions.vlmModelProvider : undefined,
      });

      // Show success message
      await this.msgSrvc.show(`Document imported successfully: "${result.article.title}"`);

      // Close the modal
      this.showImportModal = false;
      this.isUploading = false;
      this.selectedFile = null;

      // Refresh the article list
      const expandedIds = this.getExpandedCollectionIds();
      await this.loadInitialData(expandedIds);

      // Navigate to the newly created article
      this.router.navigate(['/dashboard/knowledge/articles', result.article.id, 'edit']);
    } catch (error) {
      console.error('Document import failed:', error);
      await this.msgSrvc.showError(error);
      this.isUploading = false;
      this.cdRef.markForCheck();
    } finally {
      // Reset file input
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  async showCollectionSelectorForImport(event: MouseEvent): Promise<void> {
    const popover = await this.popoverCtrl.create({
      component: CollectionSelectorComponent,
      cssClass: 'collection-selector-popover',
      event,
      backdropDismiss: true,
      componentProps: {
        currentCollectionId: this.importOptions.collectionId,
        title: 'Select Collection',
        showRootOption: true,
        callback: (selectedId: string | null) => {
          this.importOptions.collectionId = selectedId;
        },
      },
    });

    await popover.present();
  }

  getCollectionNameForImport(): string {
    if (!this.importOptions.collectionId) {
      return 'None (Root)';
    }

    const collection = this.allCollections.find((c) => c.id === this.importOptions.collectionId);
    return collection?.name ?? 'Unknown';
  }
}
